<script setup lang="ts">
import { inject } from 'vue'
import { useDark, useToggle } from '@vueuse/core'
import { MoonIcon, SunIcon, ShieldCheckIcon } from '@lucide/vue'

import { IS_DISRUPTION_MESSAGE_VISIBLE } from '@/const'

import { AppDisruptionMessage } from '@app'
import { DSButton } from '@ds'

import LogoFull from '@/assets/images/logo-text.svg'
import LogoMark from '@/assets/images/logo.svg'

const isDark = useDark()
const toggleDark = useToggle(isDark)

const isDisruptionMessageVisible = inject<boolean>(IS_DISRUPTION_MESSAGE_VISIBLE, false)

const appVersion = `${__APP_VERSION__ || ''}`
</script>

<template>
  <div class="min-w-90 flex max-lg:flex-col h-full overflow-y-auto bg-card font-ds">
    <aside class="relative hidden flex-[1_1_380px] flex-col justify-between gap-10 overflow-hidden bg-sidebar p-11 text-primary-foreground lg:flex">
      <LogoFull class="relative z-10 h-7.5 w-auto self-start" />
      <div class="relative z-10 max-w-105">
        <div class="text-4xl font-bold leading-[1.1] tracking-ds-snug">
          Visibility. Insight. Control.
        </div>
        <p class="mt-4 text-base font-medium leading-relaxed text-primary-foreground/85">
          Your full wealth picture, at your fingertips.
        </p>
      </div>
      <div class="relative z-10 flex items-center gap-2 text-[13px] font-medium text-primary-foreground/75">
        <ShieldCheckIcon class="size-4 shrink-0" />
        <span>Private by design. SOC 2 Type II.</span>
      </div>
      <LogoMark class="pointer-events-none absolute -bottom-12 -right-16 h-85 w-auto text-white opacity-10" />
    </aside>

    <header class="w-full relative flex flex-col gap-5 overflow-hidden bg-sidebar px-5 pt-safe-offset-4 pb-4 text-primary-foreground lg:hidden">
      <LogoFull class="relative z-10 h-7.5 w-auto self-start" />
      <LogoMark class="pointer-events-none absolute -bottom-9 -right-6 h-27.5 w-auto text-white opacity-10" />
    </header>

    <main class="flex flex-1 lg:flex-[1.3_1_480px] flex-col bg-card">
      <AppDisruptionMessage
        v-if="isDisruptionMessageVisible"
        class="w-full text-center px-5"
      />
      <div class="flex justify-end px-4 pt-4">
        <DSButton
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Toggle dark mode"
          @click="toggleDark()"
        >
          <SunIcon
            v-if="isDark"
            class="size-4"
          />
          <MoonIcon
            v-else
            class="size-4"
          />
        </DSButton>
      </div>

      <div class="flex flex-1 items-center justify-center px-5 py-8">
        <div class="flex w-full max-w-95 flex-col gap-6">
          <slot />
        </div>
      </div>

      <footer class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-8 py-5 text-xs font-medium text-muted-foreground max-lg:justify-center">
        <span>&copy; {{ new Date().getFullYear() }} Allposit</span>
        <div class="flex items-center gap-4">
          <a
            href="https://allposit.com/policy"
            class="text-muted-foreground hover:text-foreground hover:underline underline-offset-2 decoration-muted-foreground/40"
          >
            Privacy
          </a>
          <a
            href="https://allposit.com/terms"
            class="text-muted-foreground hover:text-foreground hover:underline underline-offset-2 decoration-muted-foreground/40"
          >
            Terms
          </a>
          <span class="font-mono">{{ appVersion }}</span>
        </div>
      </footer>
    </main>
  </div>
</template>
